import {
  createTRPCClient,
  httpLink,
  retryLink,
  type TRPCClient,
  type TRPCLink,
} from '@trpc/client';
import { observable } from '@trpc/server/observable';
import Cookies from 'js-cookie';
import { createContext, useRef, useState } from 'react';

import { TRPC_URL } from '../lib/config';
import { Router } from '../types/TRPC';
import {
  captureAndReportError,
  captureTrpcFailure,
  sanitizeForSentry,
} from '../utils/errorReporting';
import {
  normalizeMetricKey,
  trackCount,
  trackDuration,
} from '../utils/sentryMetrics';
import { COOKIE_NAME } from './AuthState';

const TRPC_REQUEST_COUNT_METRIC = 'portal.web.trpc.request';
const TRPC_REQUEST_DURATION_METRIC = 'portal.web.trpc.request.duration';
type RouterClient = TRPCClient<Router>;

export function extractHttpStatus(error: unknown): number | undefined {
  const candidate = error as {
    cause?: { status?: unknown };
    data?: { httpStatus?: unknown };
    meta?: { response?: { status?: unknown } };
    status?: unknown;
  };
  const status =
    candidate?.data?.httpStatus ??
    candidate?.cause?.status ??
    candidate?.meta?.response?.status ??
    candidate?.status;

  return typeof status === 'number' && Number.isFinite(status)
    ? status
    : undefined;
}

export function shouldRetryTrpcOperation({
  attempts,
  error,
  operationType,
}: {
  attempts: number;
  error: unknown;
  operationType: 'mutation' | 'query' | 'subscription';
}): boolean {
  if (operationType !== 'query' || attempts >= 2) {
    return false;
  }

  const status = extractHttpStatus(error);
  return status === 503 || status === 504;
}

const sentryLink: TRPCLink<Router> = () => {
  return ({ op, next }) =>
    observable((observer) => {
      const startTime = Date.now();
      const path = normalizeMetricKey(op.path);
      const type = normalizeMetricKey(op.type);
      let metricRecorded = false;

      const recordRequestMetric = (
        outcome: 'right' | 'left' | 'transport_error',
        status?: number,
      ) => {
        if (metricRecorded) {
          return;
        }
        metricRecorded = true;

        const durationMs = Date.now() - startTime;
        const attributes: Record<string, string | number | boolean> = {
          path,
          type,
          outcome,
        };

        if (status !== undefined) {
          attributes.status_code = status;
        }

        trackCount(TRPC_REQUEST_COUNT_METRIC, 1, attributes);
        trackDuration(TRPC_REQUEST_DURATION_METRIC, durationMs, attributes);
      };

      return next(op).subscribe({
        next(value) {
          if (
            value &&
            typeof value === 'object' &&
            '_tag' in value &&
            'left' in value &&
            (value as { _tag: string })._tag === 'Left'
          ) {
            const left = (
              value as {
                left: { name?: string; message?: string };
              }
            ).left;

            recordRequestMetric('left');
            captureTrpcFailure({
              path: op.path,
              type: op.type,
              input: op.input,
              error: left,
              durationMs: Date.now() - startTime,
            });
          } else {
            recordRequestMetric('right');
          }

          observer.next(value);
        },
        error(err) {
          const status = extractHttpStatus(err);

          recordRequestMetric('transport_error', status);

          captureAndReportError(err, {
            operation: `trpc.${op.type}`,
            path: op.path,
            durationMs: Date.now() - startTime,
            status,
            input: sanitizeForSentry(op.input),
          });
          observer.error(err);
        },
        complete() {
          observer.complete();
        },
      });
    });
};

function isJsonResponse(contentType: string | null): boolean {
  return (contentType || '').toLowerCase().includes('application/json');
}

function toBodyPreview(body: string): string {
  const normalized = body.replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return '[empty body]';
  }

  return normalized.slice(0, 140);
}

class TrpcHttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'TrpcHttpError';
  }
}

async function trpcHttpFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const response = await fetch(input, init);
  if (
    response.status >= 500 &&
    !isJsonResponse(response.headers.get('content-type'))
  ) {
    let preview = '[unavailable body]';
    try {
      preview = toBodyPreview(await response.text());
    } catch {
      // Ignore body parsing failures; status + content type are sufficient.
    }

    throw new TrpcHttpError(
      `tRPC HTTP ${response.status} returned non-JSON response: ${preview}`,
      response.status,
    );
  }

  return response;
}

const setup = (
  url: string,
  getJwt: () => string | null | undefined,
): RouterClient => {
  return createTRPCClient<Router>({
    links: [
      sentryLink,
      retryLink({
        retry: ({ attempts, error, op }) =>
          shouldRetryTrpcOperation({
            attempts,
            error,
            operationType: op.type,
          }),
        retryDelayMs: () => 250,
      }),
      httpLink({
        url,
        fetch: trpcHttpFetch,
        async headers() {
          const jwt = getJwt();
          return {
            ...(jwt ? { authorization: `Bearer ${jwt}` } : {}),
          };
        },
      }),
    ],
  });
};

export interface TRPC {
  setup(url: string, jwt?: string | null): RouterClient;
  client: RouterClient;
  isInitialized: boolean;
}

export const TRPCContext = createContext<TRPC>({
  setup: (url: string, jwt?: string | null) => setup(url, () => jwt),
  client: setup(TRPC_URL, () => null),
  isInitialized: false,
});

interface Props {
  children?: React.ReactNode;
}

export function TRPCProvider(props: Props) {
  // The result of `setup` can't be used directly with `useState` because `setup`
  // returns a Proxy, which doesn't work with useState.
  const jwtRef = useRef<string | null | undefined>(Cookies.get(COOKIE_NAME));

  const [client] = useState(() => {
    return {
      client: setup(TRPC_URL, () => jwtRef.current),
    };
  });

  return (
    <TRPCContext.Provider
      value={{
        setup: (_url: string, jwt?: string | null) => {
          jwtRef.current = jwt;
          return client.client;
        },
        client: client.client,
        isInitialized: true,
      }}
    >
      {props.children}
    </TRPCContext.Provider>
  );
}
